// Auth-Store — hält Session + Profile + Status. Session wird persistiert,
// Profile wird beim Hydratisieren erneut vom Worker geladen (`/auth/profile`).
//
// V1-Pendant: useState-Block in useAuth.js + localStorage-Direktzugriffe
// (`eve_auth_session`, `eve_session_started_at`).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthSession, UserProfile, UserRole } from '@/types/auth';

export const AUTH_STORE_KEY = 'eve_auth_v3';

export interface AuthState {
  session: AuthSession | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string;
  /** Unix-ms wann der letzte Sign-In stattfand (für „Online seit"-Anzeige) */
  sessionStartedAt: number | null;
}

export interface AuthActions {
  setSession: (session: AuthSession | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  beginSession: (session: AuthSession, startedAtMs?: number) => void;
  endSession: () => void;
  /** Erlaubt Owner-Override basierend auf Email-Match. */
  applyOwnerOverride: (ownerEmail: string | null) => void;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  session: null,
  profile: null,
  loading: true,
  error: '',
  sessionStartedAt: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSession: (session) => set({ session }),
      setProfile: (profile) => set({ profile }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      beginSession: (session, startedAtMs) => {
        set({ session, sessionStartedAt: startedAtMs ?? Date.now(), error: '' });
      },

      endSession: () => {
        set({ session: null, profile: null, sessionStartedAt: null, error: '' });
      },

      applyOwnerOverride: (ownerEmail) => {
        const profile = get().profile;
        if (!profile || !ownerEmail) return;
        if (profile.email === ownerEmail && profile.role !== 'owner') {
          set({ profile: { ...profile, role: 'owner' as UserRole } });
        }
      },
    }),
    {
      name: AUTH_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        session: state.session,
        sessionStartedAt: state.sessionStartedAt,
      }),
      version: 1,
    },
  ),
);

// ── Selektoren ───────────────────────────────────────────────────────────────

export const selectSession = (s: AuthState): AuthSession | null => s.session;
export const selectProfile = (s: AuthState): UserProfile | null => s.profile;
export const selectIsAuthenticated = (s: AuthState): boolean => !!s.session && !!s.profile;
export const selectIsOwner = (s: AuthState): boolean => s.profile?.role === 'owner';
export const selectAccessToken = (s: AuthState): string | null =>
  s.session?.access_token ?? null;
